import {
  Button,
  Container,
  DatePicker,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Table,
  Textarea,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Agp } from '../api/agp';
import { createItem, deleteItem, fetchItem, fetchItemsByIds, fetchItemsWithFilter, updateItem } from '../api/generic';
import PdfExportSalesInvoice from '../components/pdf/PdfExportSalesInvoice';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { Customer, SalesInvoice, SalesInvoiceItem } from '../models/Sales';
import { prefixCollection } from '../utils/prefix';

const SalesInvoicePage = () => {
  const alert = useAlertContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const { tenant, tenantObject, userObject } = useAuthContext();

  const [salesInvoice, setSalesInvoice] = useState<SalesInvoice | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState<string>(tenantObject?.invoiceNotes ?? '');
  const [batchMap, setBatchMap] = useState<Record<string, Batch>>({});

  const [agpBottles, setAgpBottles] = useState<Agp[]>([]);
  const [selectedBottles, setSelectedBottles] = useState<Agp[]>([]);
  const [selectedBottleQuantities, setSelectedBottleQuantities] = useState<Record<string, number>>({});

  // Fetch customers and invoice if editing
  useEffect(() => {
    const loadData = async () => {
      const customersFetched = await fetchItemsWithFilter<Customer>(prefixCollection('customers', tenant), []);
      setCustomers(customersFetched);

      if (id && id !== 'new') {
        const invoiceFetched = await fetchItem<SalesInvoice>(prefixCollection('salesInvoices', tenant), id);
        if (invoiceFetched) {
          setSalesInvoice(invoiceFetched);
          setSelectedCustomerId(invoiceFetched.customerId);
          setInvoiceDate((invoiceFetched.invoiceDate as Timestamp).toDate().toISOString().split('T')[0]);
          setNotes(invoiceFetched.notes ?? tenantObject?.invoiceNotes ?? '');
        }
      }
    };

    loadData();
  }, [id, tenant, tenantObject]);

  const fetchBatches = async () => {
    const batchIds = Array.from(new Set(agpBottles.map((agp) => agp.batchId).filter(Boolean)));

    if (batchIds.length === 0) {
      setBatchMap({});
      return;
    }

    const batchesFetched = await fetchItemsByIds<Batch>(prefixCollection('batches', tenant), batchIds);

    const map: Record<string, Batch> = {};
    for (const batch of batchesFetched) {
      map[batch.id] = batch;
    }

    setBatchMap(map);
  };

  // Fetch batches
  useEffect(() => {
    fetchBatches();
  }, [tenant, agpBottles]);

  // Fetch available AGP bottles
  const fetchAvailableBottles = async () => {
    const agpDocs = await fetchItemsWithFilter<Agp>(prefixCollection('agp', tenant), []);

    const released = agpDocs.filter((agp) => agp.releasedDate);

    // Index: batchId + name + size ➜ total sold
    const soldMap = new Map<string, number>();

    for (const agp of agpDocs) {
      if (agp.salesInvoiceId && agp.sold && agp.releasedDate) {
        const key = `${agp.batchId}|${agp.name}|${agp.size}`;
        const prev = soldMap.get(key) ?? 0;
        soldMap.set(key, prev + agp.sold);
      }
    }

    const available: Agp[] = released
      .map((agp) => {
        const key = `${agp.batchId}|${agp.name}|${agp.size}`;
        const sold = soldMap.get(key) ?? 0;
        const availableAmount = (agp.amount ?? 0) - sold;

        return {
          ...agp,
          _available: availableAmount, // non-persistent helper
        };
      })
      .filter((agp) => agp._available > 0);

    setAgpBottles(available);
  };

  useEffect(() => {
    fetchAvailableBottles();
  }, [tenant]);

  const generateInvoiceNumber = async (): Promise<string> => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');

    const invoices = await fetchItemsWithFilter<SalesInvoice>(prefixCollection('salesInvoices', tenant), []);

    const currentPeriodInvoices = invoices.filter((invoice) => {
      const invDate = (invoice.invoiceDate as Timestamp)?.toDate?.() ?? (invoice.invoiceDate as Date);
      return invDate.getFullYear() === year && invDate.getMonth() + 1 === parseInt(month);
    });

    const highestNumber = currentPeriodInvoices.reduce((max, invoice) => {
      const match = invoice.invoiceNumber.match(/HM-(\d+)$/);
      if (!match) return max;
      const number = parseInt(match[1]);
      return number > max ? number : max;
    }, 0);

    return `${year}-${month}-HM-${(highestNumber + 1).toString().padStart(3, '0')}`;
  };

  const getUnitPrice = (sizeNumber: number): number => {
    const size = sizeNumber;
    if (size >= 0.7) return 25;
    if (size >= 0.3) return 10;
    return 10;
  };

  const saveInvoice = async () => {
    if (!selectedCustomerId) {
      alert.warn('Please select a customer');
      return;
    }

    const newInvoiceId = id && id !== 'new' ? id : uuidv4();
    const invoiceNumber = id && id !== 'new' ? (salesInvoice?.invoiceNumber ?? '') : await generateInvoiceNumber();

    const newItems: SalesInvoiceItem[] = selectedBottles.map((bottle) => ({
      id: uuidv4(),
      agpBottleId: bottle.id ?? 'unknown',
      batchId: bottle.batchId,
      productName: bottle.name,
      quantity: selectedBottleQuantities[bottle.id ?? 'missing'] ?? 1,
      unitPrice: getUnitPrice(bottle.size),
    }));

    const subtotal = newItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

    const newInvoice: SalesInvoice = {
      id: newInvoiceId,
      invoiceNumber,
      invoiceDate: new Date(invoiceDate),
      customerId: selectedCustomerId,
      items: [...(salesInvoice?.items ?? []), ...newItems],
      status: 'draft',
      subtotal,
      tax: 0,
      total: subtotal,
      notes,
      createdAt: new Date(),
    };

    if (id && id !== 'new') {
      await updateItem(prefixCollection('salesInvoices', tenant), id, newInvoice);
    } else {
      await createItem(prefixCollection('salesInvoices', tenant), newInvoice);
    }

    // ➡️ Create "sold" AGPs for each bottle
    for (const bottle of selectedBottles) {
      const quantity = selectedBottleQuantities[bottle.id ?? 'missing'] ?? 1;

      await createItem<Agp>(prefixCollection('agp', tenant), {
        batchId: bottle.batchId,
        name: bottle.name,
        size: bottle.size,
        amount: 0,
        createdDate: new Date(),
        releasedDate: bottle.releasedDate, // keep same
        sold: quantity,
        salesInvoiceId: newInvoiceId,
        createdBy: userObject?.uid,
      });
    }

    navigate('/sales');
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!salesInvoice) return;

    const item = salesInvoice.items.find((i) => i.id === itemId);
    if (!item) return;

    try {
      // Step 1: Adjust original AGP
      if (item.agpBottleId) {
        const agpData = await fetchItem<Agp>(prefixCollection('agp', tenant), item.agpBottleId);

        if (agpData) {
          const updates: Partial<Agp> = {
            sold: Math.max((agpData.sold ?? 0) - item.quantity, 0),
          };

          await updateItem(prefixCollection('agp', tenant), item.agpBottleId, updates);
        }
      }

      // Step 2: Delete sold AGP linked to this invoice line
      const soldAgps = await fetchItemsWithFilter<Agp>(prefixCollection('agp', tenant), [
        { field: 'salesInvoiceId', operator: '==', value: salesInvoice.id },
      ]);

      const matchingSoldAgp = soldAgps.find((a) => a.batchId === item.batchId && a.name === item.productName);

      if (matchingSoldAgp && matchingSoldAgp.id) {
        await deleteItem(prefixCollection('agp', tenant), matchingSoldAgp.id);
      }

      // Step 3: Update invoice in Firestore
      const newItems = salesInvoice.items.filter((i) => i.id !== itemId);
      const newSubtotal = newItems.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);

      await updateItem<SalesInvoice>(prefixCollection('salesInvoices', tenant), salesInvoice.id, {
        items: newItems,
        subtotal: newSubtotal,
        total: newSubtotal, // no tax assumed
      });

      // Step 4: Update local state
      setSalesInvoice((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          items: newItems,
          subtotal: newSubtotal,
          total: newSubtotal,
        };
      });
    } catch (err) {
      console.error('Error while removing invoice item:', err);
      alert.error('Failed to properly remove product. Check console for details.');
    }

    await fetchAvailableBottles();
  };

  const handleAddBottle = async (bottle: Agp) => {
    if (!bottle.id) return;

    const bottleId = bottle.id;
    const quantity = selectedBottleQuantities[bottleId] ?? 1;
    if (quantity <= 0) return;

    const unitPrice = getUnitPrice(bottle.size);
    const newItem: SalesInvoiceItem = {
      id: uuidv4(),
      agpBottleId: bottleId,
      batchId: bottle.batchId,
      productName: bottle.name,
      quantity,
      unitPrice,
    };

    let currentInvoice = salesInvoice;

    // STEP 1: If invoice doesn't exist yet, create it first
    if (!currentInvoice) {
      const newInvoiceId = uuidv4();
      const invoiceNumber = await generateInvoiceNumber();

      const initialInvoice: SalesInvoice = {
        id: newInvoiceId,
        invoiceNumber,
        invoiceDate: new Date(invoiceDate),
        customerId: selectedCustomerId,
        items: [],
        status: 'draft',
        subtotal: 0,
        tax: 0,
        total: 0,
        notes,
        createdAt: new Date(),
      };

      await createItem(prefixCollection('salesInvoices', tenant), initialInvoice);
      currentInvoice = initialInvoice;
      setSalesInvoice(initialInvoice);
    }

    // STEP 2: Add item to invoice in Firestore
    const updatedItems = [...(currentInvoice.items ?? []), newItem];

    const newSubtotal = updatedItems.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);

    const updatedInvoice: SalesInvoice = {
      ...currentInvoice,
      items: updatedItems,
      subtotal: newSubtotal,
      total: newSubtotal, // Add this line if missing
    };

    await updateItem(prefixCollection('salesInvoices', tenant), currentInvoice.id, updatedInvoice);
    setSalesInvoice(updatedInvoice);

    // STEP 3: Create AGP record for the sale
    await createItem<Agp>(prefixCollection('agp', tenant), {
      batchId: bottle.batchId,
      name: bottle.name,
      size: bottle.size,
      amount: quantity,
      createdDate: new Date(),
      releasedDate: bottle.releasedDate,
      sold: quantity,
      salesInvoiceId: updatedInvoice.id,
      createdBy: userObject?.uid,
    });

    // Clear input
    setSelectedBottleQuantities((prev) => ({
      ...prev,
      [bottleId]: 0,
    }));

    await fetchAvailableBottles();
  };

  const handleDelete = async () => {
    if (!salesInvoice?.id) return;

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this invoice? All associated sales will be reverted.',
    );
    if (!confirmDelete) return;

    try {
      // 1. Find and delete AGPs created by this invoice
      const soldAgps = await fetchItemsWithFilter<Agp>(prefixCollection('agp', tenant), [
        {
          field: 'salesInvoiceId',
          operator: '==',
          value: salesInvoice.id,
        },
      ]);

      for (const agp of soldAgps) {
        if (agp.id) {
          await deleteItem(prefixCollection('agp', tenant), agp.id);
        }
      }

      // 2. Revert sold counters on original AGPs
      for (const item of salesInvoice.items) {
        if (!item.agpBottleId) continue;

        const agpData = await fetchItem<Agp>(prefixCollection('agp', tenant), item.agpBottleId);

        if (agpData) {
          const shouldClearSalesInvoiceId = agpData.salesInvoiceId === salesInvoice.id;

          const updates: Partial<Agp> = {
            sold: Math.max((agpData.sold ?? 0) - item.quantity, 0),
          };

          if (shouldClearSalesInvoiceId) {
            updates.salesInvoiceId = undefined;
          }

          await updateItem(prefixCollection('agp', tenant), item.agpBottleId, updates);
        }
      }

      // 3. Delete the invoice itself
      await deleteItem(prefixCollection('salesInvoices', tenant), salesInvoice.id);

      navigate('/sales');
    } catch (err) {
      console.error('Error reverting AGP sales or deleting invoice:', err);
      alert.error('Failed to delete invoice properly. Check console for details.');
    }
  };

  const getAvailableAmount = (item: Agp): number => {
    return (item as any)._available ?? item.amount ?? 0;
  };

  return (
    <Container
      header={
        <Header
          variant="h1"
          description={id === 'new' ? 'Create new sales invoice' : 'Edit sales invoice'}
          actions={
            <>
              {salesInvoice && (
                <PdfExportSalesInvoice
                  invoice={salesInvoice}
                  customer={customers.find((c) => c.id === salesInvoice.customerId)}
                  batches={batchMap}
                />
              )}
            </>
          }
        >
          {id === 'new' ? 'Create Sales Invoice' : (salesInvoice?.invoiceNumber ?? 'Sales Invoice')}
        </Header>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        <FormField label="Customer">
          <Select
            selectedOption={{
              label: customers.find((c) => c.id === selectedCustomerId)?.name ?? 'Select customer',
              value: selectedCustomerId,
            }}
            onChange={({ detail }) => setSelectedCustomerId(detail.selectedOption.value ?? '')}
            options={customers.map((c) => ({ label: c.name, value: c.id }))}
            placeholder="Select a customer"
          />
        </FormField>

        <FormField label="Invoice Date" constraintText="YYYY-MM-DD">
          <DatePicker value={invoiceDate} onChange={({ detail }) => setInvoiceDate(detail.value)} />
        </FormField>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={({ detail }) => setNotes(detail.value)}
            placeholder="Any special notes..."
          />
        </FormField>

        {salesInvoice && salesInvoice?.items?.length > 0 && (
          <FormField label="Existing Invoice Items">
            <Table
              items={salesInvoice.items}
              columnDefinitions={[
                {
                  id: 'productName',
                  header: 'Product',
                  cell: (item) => item.productName,
                },
                {
                  id: 'batch',
                  header: 'Batch',
                  cell: (item) => batchMap[item.batchId]?.name ?? '-',
                },
                {
                  id: 'mead',
                  header: 'Mead',
                  cell: (item) => batchMap[item.batchId]?.recipe?.name ?? '-',
                },
                {
                  id: 'quantity',
                  header: 'Quantity',
                  cell: (item) => item.quantity.toString(),
                },
                {
                  id: 'unitPrice',
                  header: `Unit Price (${tenantObject?.currency ?? '€'})`,
                  cell: (item) => item.unitPrice.toFixed(2),
                },
                {
                  id: 'total',
                  header: `Total (${tenantObject?.currency ?? '€'})`,
                  cell: (item) => (item.quantity * item.unitPrice).toFixed(2),
                },
                {
                  id: 'remove',
                  header: '',
                  cell: (item) => (
                    <Button variant="icon" iconName="close" onClick={() => handleRemoveItem(item.id)}>
                      Remove
                    </Button>
                  ),
                },
              ]}
              trackBy="id"
            />
          </FormField>
        )}

        <FormField label="Add Bottles to Invoice">
          <Table
            items={agpBottles}
            selectionType="multi"
            selectedItems={selectedBottles}
            onSelectionChange={({ detail }) => setSelectedBottles(detail.selectedItems)}
            columnDefinitions={[
              { id: 'name', header: 'Name', cell: (item) => item.name },
              {
                id: 'batch',
                header: 'Batch',
                cell: (item) => batchMap[item.batchId]?.name ?? '-',
              },
              {
                id: 'mead',
                header: 'Mead',
                cell: (item) => batchMap[item.batchId]?.recipe?.name ?? '-',
              },
              {
                id: 'size',
                header: 'Size (L)',
                cell: (item) => `${item.size}`,
              },
              {
                id: 'available',
                header: 'Available',
                cell: (item) => getAvailableAmount(item),
              },
              {
                id: 'quantity',
                minWidth: 200,
                header: 'Add',
                cell: (item) =>
                  selectedBottles.find((b) => b.id === item.id) ? (
                    <SpaceBetween size="xs" direction="horizontal">
                      <Input
                        value={selectedBottleQuantities[item.id ?? 'missing']?.toString() ?? ''}
                        type="number"
                        onChange={({ detail }) => {
                          const value = parseInt(detail.value);
                          if (isNaN(value)) return;
                          setSelectedBottleQuantities((prev) => ({
                            ...prev,
                            [item.id ?? 'missing']: Math.min(value, getAvailableAmount(item)),
                          }));
                        }}
                        placeholder={`Max ${getAvailableAmount(item)}`}
                      />
                      <Button
                        variant="primary"
                        iconName="add-plus"
                        onClick={() => handleAddBottle(item)}
                        disabled={getAvailableAmount(item) <= 0}
                      >
                        Add
                      </Button>
                    </SpaceBetween>
                  ) : (
                    '-'
                  ),
              },
            ]}
            trackBy="id"
            empty="No available bottles"
          />
        </FormField>

        <SpaceBetween size="m" direction="horizontal">
          <Button onClick={() => navigate('/sales')}>Cancel</Button>
          {id && id !== 'new' && (
            <Button onClick={handleDelete} variant="normal">
              Delete
            </Button>
          )}
          <Button variant="primary" onClick={saveInvoice}>
            Save
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

export default SalesInvoicePage;
